import React, { useMemo } from 'react';
import type { TableColumnDef } from '../../../shared/table/types.ts';
import { AppDataTable } from '../../../shared/table/AppDataTable.tsx';
import { useControllerTestSummary } from '../../../hooks/analytics/useControllerReports.ts';
import { formatDate, ProgressBar, RefreshMeta, StatusChip } from './shared.tsx';
import type { ControllerTestSummaryRow } from '../../../request/analytics/controllerReports.ts';

/**
 * Report #20 — Control Test Summary. Full data table, one row per control.
 * Accepts an optional `rows` override so the Controller dashboard can feed it
 * the slicer-filtered rows (it's the persona's primary dataset); standalone
 * usage (self-serve grid) omits `rows` and falls back to its own fetch.
 */
export const TestSummaryTable: React.FC<{ rows?: ControllerTestSummaryRow[] }> = ({ rows: rowsOverride }) => {
  const { data: fetched, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useControllerTestSummary();
  const data = rowsOverride ?? fetched;

  const columns = useMemo<TableColumnDef<ControllerTestSummaryRow>[]>(
    () => [
      {
        id: 'control_id',
        columnLabel: 'Control',
        header: () => 'Control',
        cell: ({ row }) => <span className="font-medium text-gray-900">{row.original.control_id}</span>,
        widthPercent: 12,
      },
      {
        id: 'title',
        columnLabel: 'Title',
        header: () => 'Title',
        cell: ({ row }) => row.original.title,
        widthPercent: 26,
      },
      {
        id: 'workflow_status',
        columnLabel: 'Workflow Status',
        header: () => 'Workflow Status',
        cell: ({ row }) => <StatusChip value={row.original.workflow_status} />,
        widthPercent: 14,
      },
      {
        id: 'conclusion',
        columnLabel: 'Conclusion',
        header: () => 'Conclusion',
        cell: ({ row }) => <StatusChip value={row.original.conclusion} fallback="Not tested" />,
        widthPercent: 12,
      },
      {
        id: 'attributes',
        columnLabel: 'Attributes P/F',
        header: () => 'Attributes P/F',
        cell: ({ row }) => (
          <span>
            <span className="text-green-600 font-medium">{row.original.attributes_passed ?? 0}</span>
            {' / '}
            <span className="text-red-600 font-medium">{row.original.attributes_failed ?? 0}</span>
          </span>
        ),
        widthPercent: 10,
      },
      {
        id: 'total_exceptions',
        columnLabel: 'Exceptions',
        header: () => 'Exceptions',
        cell: ({ row }) => row.original.total_exceptions ?? 0,
        widthPercent: 8,
      },
      {
        id: 'confidence_score',
        columnLabel: 'Confidence',
        header: () => 'Confidence',
        cell: ({ row }) => {
          const score = row.original.confidence_score;
          if (score == null) return <span className="text-gray-400">—</span>;
          return (
            <div className="flex items-center gap-2 min-w-[90px]">
              <ProgressBar pct={score * 100} />
              <span className="text-xs text-gray-600 w-9 text-right">{Math.round(score * 100)}%</span>
            </div>
          );
        },
        widthPercent: 12,
      },
      {
        id: 'period',
        columnLabel: 'Testing Period',
        header: () => 'Testing Period',
        cell: ({ row }) =>
          `${formatDate(row.original.testing_period_start)} – ${formatDate(row.original.testing_period_end)}`,
        widthPercent: 16,
      },
    ],
    []
  );

  return (
    <AppDataTable<ControllerTestSummaryRow>
      data={data ?? []}
      columns={columns}
      loading={isLoading}
      error={isError ? error?.message ?? 'Failed to load control test summary.' : null}
      emptyMessage="No controls found."
      searchable
      searchPlaceholder="Search controls..."
      paginated
      defaultPageSize={10}
      showPageSizeSelector
      headerStart={
        <div className="flex w-full justify-end">
          <RefreshMeta updatedAt={dataUpdatedAt} onRefresh={refetch} isFetching={isFetching} />
        </div>
      }
    />
  );
};

export default TestSummaryTable;
